import { toSupabasePublicImageThumbUrl } from "@/lib/image-thumbnails"
import { resolveStorageUrl } from "@/lib/storage/public-url"
import { formatPrice } from "@/lib/utils"

export const CANVAS_W = 1080
export const CANVAS_H = 1920
export const IMAGE_HEIGHT = CANVAS_H * 0.6

export const LOGO_TEXT = "Kraahl"
export const LOGO_POSITION: "top-left" | "top-right" | "bottom-left" | "bottom-right" | "none" = "bottom-right"

export const COLORS = {
  background: "#000000",
  placeholder: "#1a1a1a",
  white: "#ffffff",
  mutedLabel: "#888888",
  subtle: "#aaaaaa",
  hint: "#555555",
  caption: "#666666",
} as const

export const BADGE_PALETTE = {
  auction: { bg: "#FF3B30", fg: "#ffffff" },
  preorder: { bg: "#3B82F6", fg: "#ffffff" },
  offerHybrid: { bg: "#F59E0B", fg: "#111111" },
  offerOnly: { bg: "#10B981", fg: "#ffffff" },
  scheduled: { bg: "#8B5CF6", fg: "#ffffff" },
} as const

export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.arcTo(x + w, y, x + w, y + r, r)
  ctx.lineTo(x + w, y + h - r)
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r)
  ctx.lineTo(x + r, y + h)
  ctx.arcTo(x, y + h, x, y + h - r, r)
  ctx.lineTo(x, y + r)
  ctx.arcTo(x, y, x + r, y, r)
  ctx.closePath()
}

export function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  maxLines = Number.POSITIVE_INFINITY,
): number {
  const words = text.split(/\s+/).filter(Boolean)
  if (words.length === 0) return y

  const lines: string[] = []
  let line = ""

  const fitWithEllipsis = (raw: string): string => {
    let candidate = raw.trim()
    if (ctx.measureText(candidate).width <= maxWidth) return candidate
    while (candidate.length > 0 && ctx.measureText(`${candidate}…`).width > maxWidth) {
      candidate = candidate.slice(0, -1).trimEnd()
    }
    return candidate.length > 0 ? `${candidate}…` : "…"
  }

  for (let i = 0; i < words.length; i++) {
    const word = words[i]
    const next = line ? `${line} ${word}` : word
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line)
      if (lines.length === maxLines - 1) {
        const rest = words.slice(i).join(" ")
        lines.push(fitWithEllipsis(rest))
        line = ""
        break
      }
      line = word
    } else {
      line = next
    }
  }
  if (line && lines.length < maxLines) lines.push(line)

  let currentY = y
  for (const text of lines) {
    ctx.fillText(text, x, currentY)
    currentY += lineHeight
  }
  return currentY
}

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error("Failed to load image"))
    img.src = src
  })
}

export function createStoryCanvas(): {
  canvas: HTMLCanvasElement
  ctx: CanvasRenderingContext2D
} {
  const canvas = document.createElement("canvas")
  canvas.width = CANVAS_W
  canvas.height = CANVAS_H
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("Canvas 2d not available")
  ctx.fillStyle = COLORS.background
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H)
  return { canvas, ctx }
}

/**
 * NOT a preset call, deliberately. `CANVAS_W x IMAGE_HEIGHT` is the pixel size
 * of the region this image is `drawImage`'d into on the exported 1080x1920
 * story PNG — an export dimension, not a box on a screen at some DPR. Asking
 * for a preset width would just make the canvas rescale it a second time. Same
 * class as OG / social-meta images, exempted for the same reason.
 *
 * @see lib/images/presets.ts — the preset table this sits outside of, and why
 * @see .tondocs/images/HANDOFF_IMAGE_PIPELINE_STANDARDISATION_2026_07_28.md §11
 */
export function resolveStoryHeroImageUrl(url: string | undefined): string | undefined {
  if (!url) return undefined
  return toSupabasePublicImageThumbUrl(url, CANVAS_W, 80, "cover", Math.round(IMAGE_HEIGHT))
}

function profileInitials(username: string): string {
  const handle = username.replace(/^@+/, "").trim()
  if (!handle) return "?"
  const parts = handle.split(/[\s._-]+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase()
  return handle.slice(0, 2).toUpperCase()
}

function drawInitialsDisc(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number,
  initials: string,
): void {
  const grad = ctx.createLinearGradient(centerX - radius, centerY - radius, centerX + radius, centerY + radius)
  grad.addColorStop(0, "#6366f1")
  grad.addColorStop(0.48, "#a855f7")
  grad.addColorStop(1, "#ec4899")
  ctx.fillStyle = grad
  ctx.fillRect(centerX - radius, centerY - radius, radius * 2, radius * 2)
  ctx.font = `bold ${Math.round(radius * 0.72)}px Inter, system-ui, sans-serif`
  ctx.fillStyle = COLORS.white
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  ctx.fillText(initials, centerX, centerY)
}

export async function drawProfileHero(
  ctx: CanvasRenderingContext2D,
  avatarUrl: string | undefined,
  username: string,
): Promise<void> {
  const resolvedUrl = resolveStoryHeroImageUrl(avatarUrl)
  if (resolvedUrl) {
    await drawHeroImage(ctx, resolvedUrl, { blur: true, darken: 0.5 })
  } else {
    drawProfileHeroPlaceholder(ctx)
  }

  const centerX = CANVAS_W / 2
  const centerY = IMAGE_HEIGHT * 0.46
  const radius = 220
  const ringWidth = 8

  ctx.save()
  ctx.beginPath()
  ctx.arc(centerX, centerY, radius + ringWidth, 0, Math.PI * 2)
  ctx.fillStyle = "rgba(255,255,255,0.35)"
  ctx.fill()
  ctx.restore()

  ctx.save()
  ctx.beginPath()
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2)
  ctx.clip()

  if (resolvedUrl) {
    try {
      const img = await loadImage(resolvedUrl)
      const scale = Math.max((radius * 2) / img.width, (radius * 2) / img.height)
      const drawW = img.width * scale
      const drawH = img.height * scale
      ctx.drawImage(img, centerX - drawW / 2, centerY - drawH / 2, drawW, drawH)
    } catch {
      drawInitialsDisc(ctx, centerX, centerY, radius, profileInitials(username))
    }
  } else {
    drawInitialsDisc(ctx, centerX, centerY, radius, profileInitials(username))
  }
  ctx.restore()
}

export function drawProfileHeroPlaceholder(ctx: CanvasRenderingContext2D): void {
  const grad = ctx.createLinearGradient(0, 0, CANVAS_W, IMAGE_HEIGHT)
  grad.addColorStop(0, "#6366f1")
  grad.addColorStop(0.48, "#a855f7")
  grad.addColorStop(1, "#ec4899")
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, CANVAS_W, IMAGE_HEIGHT)
  const fade = ctx.createLinearGradient(0, IMAGE_HEIGHT - 400, 0, IMAGE_HEIGHT)
  fade.addColorStop(0, "rgba(0,0,0,0)")
  fade.addColorStop(1, "rgba(0,0,0,1)")
  ctx.fillStyle = fade
  ctx.fillRect(0, IMAGE_HEIGHT - 400, CANVAS_W, 400)
}

export async function drawHeroImage(
  ctx: CanvasRenderingContext2D,
  rawImageUrl: string | undefined,
  opts: { blur?: boolean; darken?: number } = {},
): Promise<void> {
  // `loadImage` sets `img.src` and needs an absolute URL — a bucket-relative
  // storage path would resolve against the site domain and 404 silently.
  // Idempotent, so the avatar path (already thumb-resolved) is unaffected.
  // @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §3 L3
  const imageUrl = rawImageUrl ? resolveStorageUrl(rawImageUrl) : rawImageUrl;
  if (!imageUrl) {
    ctx.fillStyle = COLORS.placeholder
    ctx.fillRect(0, 0, CANVAS_W, IMAGE_HEIGHT)
    return
  }

  try {
    const img = await loadImage(imageUrl)
    const scale = Math.max(CANVAS_W / img.width, IMAGE_HEIGHT / img.height)
    const drawW = img.width * scale
    const drawH = img.height * scale
    const drawX = (CANVAS_W - drawW) / 2
    const drawY = (IMAGE_HEIGHT - drawH) / 2
    ctx.save()
    ctx.rect(0, 0, CANVAS_W, IMAGE_HEIGHT)
    ctx.clip()
    if (opts.blur) ctx.filter = "blur(24px)"
    ctx.drawImage(img, drawX, drawY, drawW, drawH)
    ctx.filter = "none"
    ctx.restore()

    if (opts.darken && opts.darken > 0) {
      ctx.fillStyle = `rgba(0,0,0,${Math.min(0.95, Math.max(0, opts.darken))})`
      ctx.fillRect(0, 0, CANVAS_W, IMAGE_HEIGHT)
    }

    const grad = ctx.createLinearGradient(0, IMAGE_HEIGHT - 400, 0, IMAGE_HEIGHT)
    grad.addColorStop(0, "rgba(0,0,0,0)")
    grad.addColorStop(1, "rgba(0,0,0,1)")
    ctx.fillStyle = grad
    ctx.fillRect(0, IMAGE_HEIGHT - 400, CANVAS_W, 400)
  } catch {
    ctx.fillStyle = COLORS.placeholder
    ctx.fillRect(0, 0, CANVAS_W, IMAGE_HEIGHT)
  }
}

export function drawLogo(ctx: CanvasRenderingContext2D): void {
  if (LOGO_POSITION === "none") return
  const pad = 60
  const logoFontSize = 56
  ctx.save()
  ctx.font = `bold ${logoFontSize}px Inter, system-ui, sans-serif`
  ctx.fillStyle = COLORS.white
  ctx.shadowColor = "rgba(0,0,0,0.7)"
  ctx.shadowBlur = 20
  if (LOGO_POSITION === "top-left") {
    ctx.textAlign = "left"
    ctx.fillText(LOGO_TEXT, pad, 88)
  } else if (LOGO_POSITION === "top-right") {
    ctx.textAlign = "right"
    ctx.fillText(LOGO_TEXT, CANVAS_W - pad, 88)
  } else if (LOGO_POSITION === "bottom-left") {
    ctx.textAlign = "left"
    ctx.fillText(LOGO_TEXT, pad, IMAGE_HEIGHT - 40)
  } else {
    ctx.textAlign = "right"
    ctx.fillText(LOGO_TEXT, CANVAS_W - pad, CANVAS_H - 50)
  }
  ctx.restore()
}

export interface BadgeSpec {
  label: string
  bg: string
  fg: string
}

export function drawBadge(
  ctx: CanvasRenderingContext2D,
  badge: BadgeSpec,
  x: number,
  y: number,
): { height: number } {
  const paddingX = 28
  const paddingY = 14
  const fontSize = 30
  ctx.save()
  ctx.font = `bold ${fontSize}px Inter, system-ui, sans-serif`
  ctx.textBaseline = "alphabetic"
  const label = badge.label.toUpperCase()
  const textWidth = ctx.measureText(label).width
  const boxW = textWidth + paddingX * 2
  const boxH = fontSize + paddingY * 2
  ctx.fillStyle = badge.bg
  roundRect(ctx, x, y, boxW, boxH, boxH / 2)
  ctx.fill()
  ctx.fillStyle = badge.fg
  ctx.textAlign = "left"
  ctx.fillText(label, x + paddingX, y + fontSize + paddingY - 6)
  ctx.restore()
  return { height: boxH }
}

export interface OverlayPill {
  label: string
  bg: string
  fg: string
  border?: string
}

export const OVERLAY_PILL_BORDER = "rgba(255,255,255,0.22)"

export const OVERLAY_PILL_PRESETS = {
  neutral: {
    bg: "rgba(0,0,0,0.55)",
    fg: "#ffffff",
    border: OVERLAY_PILL_BORDER,
  },
  amber: {
    bg: "#F59E0B",
    fg: "#ffffff",
    border: OVERLAY_PILL_BORDER,
  },
} as const

function drawPill(
  ctx: CanvasRenderingContext2D,
  pill: OverlayPill,
  x: number,
  y: number,
): { width: number; height: number } {
  const paddingX = 28
  const paddingY = 14
  const fontSize = 30
  ctx.save()
  ctx.font = `bold ${fontSize}px Inter, system-ui, sans-serif`
  ctx.textBaseline = "alphabetic"
  const textWidth = ctx.measureText(pill.label).width
  const boxW = textWidth + paddingX * 2
  const boxH = fontSize + paddingY * 2
  ctx.fillStyle = pill.bg
  roundRect(ctx, x, y, boxW, boxH, boxH / 2)
  ctx.fill()
  if (pill.border) {
    ctx.lineWidth = 2
    ctx.strokeStyle = pill.border
    roundRect(ctx, x, y, boxW, boxH, boxH / 2)
    ctx.stroke()
  }
  ctx.fillStyle = pill.fg
  ctx.textAlign = "left"
  ctx.fillText(pill.label, x + paddingX, y + fontSize + paddingY - 6)
  ctx.restore()
  return { width: boxW, height: boxH }
}

export function drawTopOverlay(
  ctx: CanvasRenderingContext2D,
  rows: OverlayPill[][],
  x: number,
  y: number,
): void {
  const rowGap = 14
  const inlineGap = 12
  let currentY = y
  for (const row of rows) {
    if (row.length === 0) continue
    let cursorX = x
    let rowHeight = 0
    for (const pill of row) {
      const { width, height } = drawPill(ctx, pill, cursorX, currentY)
      cursorX += width + inlineGap
      rowHeight = Math.max(rowHeight, height)
    }
    currentY += rowHeight + rowGap
  }
}

export interface SellerQrLayout {
  qrX: number
  qrY: number
  qrSize: number
  leftColRight: number
  leftContentMaxWidth: number
  contentY: number
  showOnKraahlLine: boolean
}

export function resolveBottomLayout(): SellerQrLayout {
  const contentY = IMAGE_HEIGHT + 40
  const qrSize = 280
  const qrMarginRight = 60
  const gap = 40
  const qrX = CANVAS_W - qrMarginRight - qrSize
  const leftColRight = qrX - gap
  const leftContentMaxWidth = leftColRight - 60
  return {
    contentY,
    qrX,
    qrY: contentY,
    qrSize,
    leftColRight,
    leftContentMaxWidth,
    showOnKraahlLine: LOGO_POSITION !== "bottom-right",
  }
}

export async function drawQrBlock(
  ctx: CanvasRenderingContext2D,
  listingUrl: string | undefined,
  sellerLabel: string | null,
  layout: SellerQrLayout,
  opts?: { scanLine?: string },
): Promise<void> {
  const scanLine = opts?.scanLine ?? "Scan to view listing"
  if (listingUrl) {
    const qrSizePx = 320
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${qrSizePx}x${qrSizePx}&data=${encodeURIComponent(
      listingUrl,
    )}&bgcolor=0d0d0d&color=ffffff&margin=0`
    try {
      const qrImg = await loadImage(qrUrl)
      ctx.save()
      ctx.fillStyle = "rgba(255,255,255,0.06)"
      roundRect(ctx, layout.qrX - 16, layout.qrY - 16, layout.qrSize + 32, layout.qrSize + 32, 20)
      ctx.fill()
      ctx.drawImage(qrImg, layout.qrX, layout.qrY, layout.qrSize, layout.qrSize)
      const qrCenterX = layout.qrX + layout.qrSize / 2
      ctx.font = "24px Inter, system-ui, sans-serif"
      ctx.fillStyle = COLORS.caption
      ctx.textAlign = "center"
      ctx.fillText(scanLine, qrCenterX, layout.qrY + layout.qrSize + 48)
      if (layout.showOnKraahlLine) {
        ctx.font = "20px Inter, system-ui, sans-serif"
        ctx.fillStyle = COLORS.hint
        ctx.fillText("on kraahl.com", qrCenterX, layout.qrY + layout.qrSize + 80)
      }
      ctx.restore()
      return
    } catch {
      /* fall through to footer */
    }
  }

  ctx.save()
  ctx.fillStyle = "rgba(255,255,255,0.06)"
  roundRect(ctx, 40, CANVAS_H - 180, CANVAS_W - 80, 80, 40)
  ctx.fill()
  ctx.font = "bold 30px Inter, system-ui, sans-serif"
  ctx.fillStyle = COLORS.white
  ctx.textAlign = "center"
  ctx.fillText(sellerLabel ? `${sellerLabel} on Kraahl` : "Kraahl", CANVAS_W / 2, CANVAS_H - 130)
  ctx.font = "24px Inter, system-ui, sans-serif"
  ctx.fillStyle = COLORS.hint
  ctx.fillText(scanLine, CANVAS_W / 2, CANVAS_H - 72)
  if (layout.showOnKraahlLine) {
    ctx.font = "20px Inter, system-ui, sans-serif"
    ctx.fillText("on kraahl.com", CANVAS_W / 2, CANVAS_H - 44)
  }
  ctx.restore()
}

export function resolveSellerLabel(seller: string | undefined): string | null {
  if (!seller) return null
  const trimmed = seller.trim()
  if (!trimmed) return null
  if (trimmed.toLowerCase() === "you") return null
  return trimmed.startsWith("@") ? trimmed : `@${trimmed}`
}

export function drawSellerLine(
  ctx: CanvasRenderingContext2D,
  sellerLabel: string | null,
  x: number,
  y: number,
): number {
  if (!sellerLabel) return y
  ctx.save()
  ctx.font = "bold 36px Inter, system-ui, sans-serif"
  ctx.fillStyle = COLORS.subtle
  ctx.textAlign = "left"
  ctx.fillText(sellerLabel, x, y + 12)
  ctx.restore()
  return y + 12 + 36
}

export interface TextBlock {
  text: string
  font: string
  color: string
  maxLines?: number
  lineHeight?: number
  marginTop?: number
}

export function drawTextColumn(
  ctx: CanvasRenderingContext2D,
  blocks: TextBlock[],
  x: number,
  startY: number,
  maxWidth: number,
): number {
  let y = startY
  for (const block of blocks) {
    y += block.marginTop ?? 0
    ctx.save()
    ctx.font = block.font
    ctx.fillStyle = block.color
    ctx.textAlign = "left"
    const lineHeight = block.lineHeight ?? parseFontLineHeight(block.font)
    y = drawWrappedText(ctx, block.text, x, y, maxWidth, lineHeight, block.maxLines ?? Number.POSITIVE_INFINITY)
    ctx.restore()
  }
  return y
}

function parseFontLineHeight(font: string): number {
  const match = font.match(/(\d+(?:\.\d+)?)px/)
  if (!match) return 48
  const size = Number.parseFloat(match[1])
  return Math.round(size * 1.2)
}

export { formatPrice }
